import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { api, type Artifact } from '../api/client';
import { ARTIFACT_ORIGIN } from '../env';
import posthog from '../lib/posthog';

interface ArtifactActionsMenuProps {
  artifact: Pick<Artifact, 'id' | 'latest_version_id' | 'status'>;
  onChanged?: () => void;
  onShare?: () => void;
  canArchive?: boolean;
  buttonLabel?: string;
  buttonTitle?: string;
  icon?: 'kebab' | 'burger';
  resolveDownloadHref?: (artifact: Pick<Artifact, 'id' | 'latest_version_id' | 'status'>) => Promise<string | null>;
}

export default function ArtifactActionsMenu({
  artifact,
  onChanged,
  onShare,
  canArchive = false,
  buttonLabel,
  buttonTitle = 'Artifact actions',
  icon = 'kebab',
  resolveDownloadHref,
}: ArtifactActionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event: MouseEvent | globalThis.MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((current) => !current);
  };

  const handleDownload = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy || !artifact.latest_version_id) return;

    setBusy(true);
    try {
      const href = resolveDownloadHref
        ? await resolveDownloadHref(artifact)
        : await (async () => {
            const session = await api.createArtifactAccessSession(artifact.id);
            return `${ARTIFACT_ORIGIN}/download/${session.token}/${artifact.id}/${artifact.latest_version_id}`;
          })();
      if (!href) return;
      const link = document.createElement('a');
      link.href = href;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleArchiveToggle = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy || !canArchive) return;

    setBusy(true);
    try {
      if (artifact.status === 'archived') {
        await api.unarchiveArtifact(artifact.id);
        posthog.capture('artifact_unarchived', { artifact_id: artifact.id });
      } else {
        await api.archiveArtifact(artifact.id);
        posthog.capture('artifact_archived', { artifact_id: artifact.id });
      }
      setOpen(false);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const isArchived = artifact.status === 'archived';

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="h-[32px] min-w-[32px] px-[var(--sp-1)] inline-flex items-center justify-center gap-[var(--sp-1)] rounded-[var(--radius-md)] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
        style={{
          color: open ? 'var(--rd-text-strong)' : 'var(--rd-text-meta)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--rd-text-strong)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.color = 'var(--rd-text-meta)'; }}
        aria-label={buttonTitle}
        title={buttonTitle}
      >
        {icon === 'burger' ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
            <path d="M3 4.5h10M3 8h10M3 11.5h10" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="3.25" r="1.25" />
            <circle cx="8" cy="8" r="1.25" />
            <circle cx="8" cy="12.75" r="1.25" />
          </svg>
        )}
        {buttonLabel && <span className="text-[13px] font-semibold">{buttonLabel}</span>}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-[var(--sp-1)] z-30 min-w-[180px] rounded-[var(--radius-md)] border p-[var(--sp-1)]"
          style={{
            background: 'var(--rd-surface-panel)',
            borderColor: 'var(--rd-border)',
            boxShadow: 'var(--rd-dropdown-shadow)',
          }}
        >
          {onShare && (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
                onShare();
              }}
              className="w-full h-[36px] px-[var(--sp-3)] rounded-[var(--radius-sm)] border-0 bg-transparent text-left text-[13px] font-medium cursor-pointer inline-flex items-center gap-[var(--sp-2)] transition-colors duration-[120ms]"
              style={{ color: 'var(--rd-text-medium)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
                <circle cx="12" cy="3.5" r="2" /><circle cx="4" cy="8" r="2" /><circle cx="12" cy="12.5" r="2" />
                <path d="M5.8 7l4.4-2.5M5.8 9l4.4 2.5" />
              </svg>
              <span>Share</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={busy || !artifact.latest_version_id}
            className="w-full h-[36px] px-[var(--sp-3)] rounded-[var(--radius-sm)] border-0 bg-transparent text-left text-[13px] font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-[var(--sp-2)]"
            style={{ color: 'var(--rd-text-medium)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 1.5v7M4.5 6.5L7 9l2.5-2.5M2 11.5h10" />
            </svg>
            <span>Download</span>
          </button>
          {canArchive && (
            <button
              type="button"
              onClick={handleArchiveToggle}
              disabled={busy}
              className="w-full h-[36px] px-[var(--sp-3)] rounded-[var(--radius-sm)] border-0 bg-transparent text-left text-[13px] font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-[var(--sp-2)]"
              style={{ color: 'var(--rd-text-medium)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {isArchived ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 3.5h10l-1 8H3l-1-8ZM5 3.5V2h4v1.5M4.5 7.5 7 5l2.5 2.5" />
                  <path d="M7 5v5" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 3.5h10l-1 8H3l-1-8ZM5 3.5V2h4v1.5M4.5 6.5 7 9l2.5-2.5" />
                  <path d="M7 9V4" />
                </svg>
              )}
              <span>{isArchived ? 'Unarchive' : 'Archive'}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
